// Helpers for formatting Shopify product metafields/metaobjects into a
// human-readable Finnish spec list.

type MediaImageRef = { image?: { url: string; altText: string | null } | null };
type MetaobjectField = {
  key: string;
  type: string;
  value: string | null;
  reference?: MediaImageRef | null;
};
type MetaobjectRef = {
  id: string;
  type: string;
  handle: string;
  fields: MetaobjectField[];
};

export type RawMetafield = {
  key: string;
  namespace: string;
  type: string;
  value: string | null;
  reference?: MediaImageRef | MetaobjectRef | null;
  references?: { edges: Array<{ node: MediaImageRef | MetaobjectRef }> } | null;
} | null;

export interface FormattedMetafield {
  key: string;
  label: string;
  value: string;
  images?: Array<{ url: string; alt: string }>;
}

const LABELS: Record<string, string> = {
  material: "Materiaali",
  materiaali: "Materiaali",
  age: "Ikäsuositus",
  age_range: "Ikäsuositus",
  "age-group": "Ikäsuositus",
  ikasuositus: "Ikäsuositus",
  ika: "Ikäsuositus",
  dimensions: "Mitat",
  mitat: "Mitat",
  weight: "Paino",
  paino: "Paino",
  color: "Väri",
  vari: "Väri",
  "color-pattern": "Väri",
  care: "Hoito-ohjeet",
  hoito: "Hoito-ohjeet",
  care_guide: "Hoito-ohjeet",
  certifications: "Sertifikaatit",
  sertifikaatit: "Sertifikaatit",
  origin: "Alkuperä",
  alkupera: "Alkuperä",
  package_contents: "Pakkauksen sisältö",
  pakkauksen_sisalto: "Pakkauksen sisältö",
  highlights: "Kohokohdat",
  kohokohdat: "Kohokohdat",
  features: "Ominaisuudet",
  ominaisuudet: "Ominaisuudet",
  safety: "Turvallisuus",
  turvallisuus: "Turvallisuus",
  warranty: "Takuu",
  takuu: "Takuu",
  assembly: "Kokoaminen",
  kokoaminen: "Kokoaminen",
  subtitle: "Lisätieto",
  "target-gender": "Kohderyhmä",
};

function labelFor(key: string) {
  return LABELS[key] ?? key.replace(/[-_]/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function tryParseJson<T = unknown>(value: string): T | null {
  try { return JSON.parse(value) as T; } catch { return null; }
}

function formatDimension(value: string): string {
  const obj = tryParseJson<{ value: number; unit: string }>(value);
  if (obj && typeof obj.value === "number") return `${obj.value} ${obj.unit}`;
  return value;
}

function formatValue(type: string, raw: string): string {
  if (type === "boolean") return raw === "true" ? "Kyllä" : "Ei";
  if (type === "list.single_line_text_field" || type === "list.color") {
    const arr = tryParseJson<string[]>(raw);
    return Array.isArray(arr) ? arr.join(", ") : raw;
  }
  if (type === "dimension" || type === "weight" || type === "volume") return formatDimension(raw);
  if (type === "rating") {
    const obj = tryParseJson<{ value: string; scale_max: string }>(raw);
    if (obj) return `${obj.value} / ${obj.scale_max}`;
    return raw;
  }
  if (type === "rich_text_field") {
    const obj = tryParseJson<{ children?: Array<{ children?: Array<{ value?: string }> }> }>(raw);
    if (obj?.children) {
      return obj.children
        .map((p) => (p.children ?? []).map((c) => c.value ?? "").join(""))
        .filter(Boolean)
        .join("\n");
    }
    return raw;
  }
  return raw;
}

function formatMetaobject(mo: MetaobjectRef): string {
  return mo.fields
    .filter((f) => f.value)
    .map((f) => `${labelFor(f.key)}: ${formatValue(f.type, f.value!)}`)
    .join(" · ");
}

export function formatMetafields(metafields: RawMetafield[] | null | undefined): FormattedMetafield[] {
  if (!metafields) return [];
  const out: FormattedMetafield[] = [];
  for (const mf of metafields) {
    if (!mf) continue;
    const label = labelFor(mf.key);
    const images: Array<{ url: string; alt: string }> = [];
    let value = "";

    if (mf.references?.edges?.length) {
      const parts: string[] = [];
      for (const { node } of mf.references.edges) {
        if ("image" in node && node.image) {
          images.push({ url: node.image.url, alt: node.image.altText ?? label });
        } else if ("fields" in node) {
          parts.push(formatMetaobject(node));
        }
      }
      value = parts.join(" • ");
    } else if (mf.reference) {
      if ("image" in mf.reference && mf.reference.image) {
        images.push({ url: mf.reference.image.url, alt: mf.reference.image.altText ?? label });
      } else if ("fields" in mf.reference) {
        value = formatMetaobject(mf.reference);
      }
    } else if (mf.value != null) {
      value = formatValue(mf.type, mf.value);
    }

    if (value || images.length) out.push({ key: `${mf.namespace}.${mf.key}`, label, value, images: images.length ? images : undefined });
  }
  return out;
}